import React from 'react';
import { useTranslation } from 'react-i18next';
import { formatTotalDuration } from '../Utility/timeHelpers';
import { CLIENT_INFO_REQUIRED, ClientInfoRequired } from './clientInfoRequired';

interface ClientInfoProps {
  clientName: string;
  setClientName: (name: string) => void;
  // EV-298。配送先の説明は null を許す（API・管理サイト・移行で作った行）。空の説明として扱う。
  clientDescription: string | null;
  setClientDescription: (description: string) => void;
  totalDurationWithTasks?: { hours: number; minutes: number } | null;
  activeTasks?: number;
  driversAssigned?: number;
  // EV-436。必須の欄の知らせ（送る画面が持つ）。無ければ aria-required だけを付ける。
  requiredFields?: ClientInfoRequired;
}

const ClientInfo: React.FC<ClientInfoProps> = ({
  clientName,
  setClientName,
  clientDescription,
  setClientDescription,
  totalDurationWithTasks,
  activeTasks,
  driversAssigned,
  requiredFields: required,
}) => {
  const { t } = useTranslation('client');
  const description = clientDescription ?? '';
  // EV-436（EV-430 と同じ）。required の空欄は WebKit が開いた直後から「無効なデータ」と読むので、
  // aria-required で伝え、空のまま送ったときだけ aria-invalid と知らせを出す。
  const fieldProps = (name: keyof typeof CLIENT_INFO_REQUIRED) =>
    required ? required.fieldProps(name) : { 'aria-required': true as const };

  return (
    <>
      <div className="flex flex-col px-6 py-4 gap-4">
        <h6 className="font-medium text-subtitle-1">
          {t('detail.client_info')}
        </h6>
      </div>
      <div className="grid grid-cols-5">
        <div className="flex flex-col col-span-2 gap-6 px-6 mb-4">
          <div className="flex flex-col gap-1">
            <label
              htmlFor="clientName"
              className="text-caption text-neutral/100"
            >
              {t('detail.client_name')} <span className="text-error">*</span> (
              {clientName.length}/30)
            </label>
            <input
              id="clientName"
              type="text"
              maxLength={30}
              value={clientName}
              onChange={(e) => {
                setClientName(e.target.value);
                required?.clear('clientName');
              }}
              className="h-10 border border-neutral/50 text-neutral/300 focus:outline-none focus:border-neutral/75 text-caption rounded py-2 px-4"
              {...fieldProps('clientName')}
            />
            {required?.message('clientName')}
          </div>
          <div className="flex flex-col gap-1">
            <label
              htmlFor="clientDescription"
              className="text-caption text-neutral/100"
            >
              {t('detail.client_description')}{' '}
              <span className="text-error">*</span> ({description.length}
              /100)
            </label>
            <textarea
              id="clientDescription"
              maxLength={100}
              value={description}
              // UAT A09-2。管理者が書くメモであることを入力例で示す。
              placeholder={t('detail.client_description_placeholder')}
              onChange={(e) => {
                setClientDescription(e.target.value);
                required?.clear('clientDescription');
              }}
              className="h-20 border border-neutral/50 text-neutral/300 focus:outline-none focus:border-neutral/75 text-caption rounded py-2 px-4 resize-none"
              {...fieldProps('clientDescription')}
            />
            {required?.message('clientDescription')}
          </div>
        </div>
        <div className="flex flex-row col-span-3 gap-10 pl-24">
          <div className="flex flex-col w-full gap-1">
            <label
              htmlFor="totalDuration"
              className="text-caption text-neutral/100"
            >
              {t('detail.total_duration')}
            </label>
            <p className="text-medium text-h5 text-neutral/300">
              {/* EV-114 / EV-115。配送先詳細と同じ表示（0時間は分、読み込み中・失敗は「-」）。 */}
              {formatTotalDuration(t, totalDurationWithTasks)}
            </p>
            {/* UAT A09-1（和田様）。数の意味（数え方）を小さく添える。 */}
            <p className="text-caption text-neutral/100">{t('detail.total_duration_hint')}</p>
          </div>
          <div className="flex flex-col w-full gap-1">
            <label
              htmlFor="activeTasks"
              className="text-caption text-neutral/100"
            >
              {t('detail.active_task')}
            </label>
            <p className="text-medium text-h5 text-neutral/300">
              {activeTasks !== undefined ? activeTasks : '-'}
            </p>
            <p className="text-caption text-neutral/100">{t('detail.active_task_hint')}</p>
          </div>
          <div className="flex flex-col w-full gap-1">
            <label
              htmlFor="driverAssigned"
              className="text-caption text-neutral/100"
            >
              {t('detail.driver_assigned')}
            </label>
            <p className="text-medium text-h5 text-neutral/300">
              {driversAssigned !== undefined ? driversAssigned : '-'}
            </p>
            <p className="text-caption text-neutral/100">{t('detail.driver_assigned_hint')}</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default ClientInfo;
